import { lazy } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";

import { MainLayout } from "./layouts";
import { AssignmentLayout } from "./layouts";
import EditMentorProfilePage from "views/ProfilePage/components/EditMentorProfilePage";
import FinancialsPage from "views/ProfilePage/components/FinancialsPage";
import OrgTestsPage from "views/ProfilePage/components/OrgTestsPage";
import OrgInviteCandidatePage from "views/ProfilePage/components/OrgInviteCandidatePage";
import OrgActivityPage from "views/ProfilePage/components/OrgActivityPage"; // Import the new page
import CompanyDetailsPage from "views/ProfilePage/components/CompanyDetailsPage/CompanyDetailsPage";
import Assignment from "components/Assignment";
import AuthGuard from "components/Guards/AuthGuard";

const IndexPage = lazy(() => import("./views"));
const ErrorPage = lazy(() => import("./views/ErrorPage"));
const ErrorLoginPage = lazy(() => import("./views/ErrorLoginPage"));
const ContactPage = lazy(() => import("./views/ContactPage"));
const LabsPage = lazy(() => import("./views/LabsPage"));
const QualificationLabPage = lazy(() => import("./views/QualificationLabPage"));
const SingleLabPage = lazy(() => import("./views/SingleLabPage"));
const LabInstancePage = lazy(() => import("./views/LabInstancePage"));
const ResultsPage = lazy(
  () => import("./views/ResultsPage"),
);
const MoreFromMentorPage = lazy(
  () => import("./views/MoreFromMentorPage"),
);
const EmbeddedCommunityPage = lazy(
  () => import("./views/EmbeddedCommunityPage"),
);
const ProfilePage = lazy(() => import("./views/ProfilePage"));
const PersonalDetailsPage = lazy(
  () => import("./views/ProfilePage/components/PersonalDetailsPage"),
);

const MessagesPage = lazy(
  () => import("./views/ProfilePage/components/MessagesPage"),
);
const FavoriteLabsPage = lazy(
  () => import("./views/ProfilePage/components/FavoriteLabsPage"),
);
const ProfileSchedulePage = lazy(
  () => import("./views/ProfilePage/components/ProfileSchedulePage"),
);
const SettingsPage = lazy(
  () => import("./views/ProfilePage/components/SettingsPage"),
);
const ManageMembershipPage = lazy(
  () => import("./views/ProfilePage/components/ManageMembershipPage"),
);
const LoginPage = lazy(() => import("./views/LoginPage"));
const Error404Page = lazy(() => import("./views/Error404Page"));

const PlaceholderOrgPage = ({ pageName }: { pageName: string }) => (
  <div className="p-4">
    <h1 className="text-2xl font-bold">{pageName} Page</h1>
    <p>This is a placeholder page.</p>
  </div>
);

const renderRoutes = () => {
  let isAssessment = false;
  try {
    const assessment_url = new URL(process.env.REACT_APP_ASSESSMENTS_URL);
    isAssessment =
      window.location.hostname === assessment_url.hostname ||
      process.env.REACT_APP_FORCE_DEBUG_ASSESSMENTS === "true";
  } catch (e) {
    console.log(e);
    isAssessment = false;
  }

  return (
    <Routes>
      {!isAssessment ? (
        <Route path="/" element={<MainLayout />}>
          <Route index element={<IndexPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="labs" element={<LabsPage />} />
          <Route path="lab/:slug" element={<SingleLabPage />} />
          <Route path="qualification-lab/:slug" element={<QualificationLabPage />} />
          <Route path="mentor/:id" element={<MoreFromMentorPage />} />
          <Route path="result/:id" element={<ResultsPage />} />

          <Route path="/" element={<AuthGuard ><Outlet /></AuthGuard>} >
            <Route path="instance/:id" element={<LabInstancePage />} />
          </Route>
          
          <Route path="profile" element={<AuthGuard><ProfilePage /></AuthGuard>} >
            <Route path="personal-details" element={<PersonalDetailsPage />} />
            <Route path="favorite-labs" element={<FavoriteLabsPage />} />
            <Route path="schedule" element={<ProfileSchedulePage />} />
            <Route path="messages" element={<MessagesPage />} />
            <Route path="manage-plan" element={<ManageMembershipPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          <Route path="profile" element={<AuthGuard requireMentor={true}><ProfilePage /></AuthGuard>} >
            <Route path="mentor-page" element={<EditMentorProfilePage />} />
            <Route path="financials" element={<FinancialsPage />} />
          </Route>

          <Route path="recruiter" element={<AuthGuard requireOrg={true}><ProfilePage /></AuthGuard>} >
            <Route path="candidates" element={<PlaceholderOrgPage pageName="Candidates" />} />
            <Route path="activity" element={<OrgActivityPage />} />
            <Route path="qualification-catalog" element={<OrgTestsPage />} />
            <Route path="templates" element={<PlaceholderOrgPage pageName="Templates" />} />
            <Route path="invite/:lab_slug" element={<OrgInviteCandidatePage />} />
            <Route path="details" element={<CompanyDetailsPage />} />
          </Route>
          
          <Route path="community" element={<EmbeddedCommunityPage />} />
          <Route path="error-404" element={<Error404Page />} />
          <Route path="error-login" element={<ErrorLoginPage />} />
          <Route path="error-login/:status" element={<ErrorLoginPage />} />
        </Route>
      ) : (
        <Route path="/" element={<AssignmentLayout />}>
          <Route index element={<Assignment />} />
          <Route
            path="error-404"
            element={<Error404Page hasBackHomeButton={false} />}
          />
        </Route>
      )}
      <Route path="error" element={<ErrorPage />} />
      <Route path="error/:status" element={<ErrorPage />} />
      <Route path="oauth2" element={<LoginPage />} />
      <Route path="*" element={<Navigate to="/error-404" replace />} />
    </Routes>
  );
};

export default renderRoutes;
